import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { showSuccess, showError } from '../utils/toast';
import './UserModal.css';

const AddonModal = ({ addon, onClose, onAddonUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    tokens: 0,
    price_usd: 0,
    is_active: true,
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (addon) {
      // Editing existing addon
      setFormData({
        name: addon.name || '',
        tokens: addon.tokens || 0,
        price_usd: addon.price_usd || 0,
        is_active: addon.is_active !== undefined ? addon.is_active : true,
        description: addon.description || ''
      });
    } else {
      // New addon - sensible defaults for $5 / 100-token top-up (25% of 400 monthly)
      setFormData({
        name: 'Usage Top-Up',
        tokens: 100,
        price_usd: 5.0,
        is_active: true,
        description: 'One-time top-up equal to 25% of monthly token allocation'
      });
    }
  }, [addon]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    let finalValue;

    if (type === 'checkbox') {
      finalValue = checked;
    } else if (type === 'number') {
      // Handle 0 correctly - parseFloat('0') is 0, not falsy
      const parsed = parseFloat(value);
      finalValue = isNaN(parsed) ? 0 : parsed;
    } else {
      finalValue = value;
    }

    setFormData({
      ...formData,
      [name]: finalValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.name || formData.name.trim().length === 0) {
        setError('Addon name is required');
        setLoading(false);
        return;
      }

      if (formData.name.trim().length > 100) {
        setError('Addon name must be 100 characters or less');
        setLoading(false);
        return;
      }

      if (formData.tokens <= 0) {
        setError('Tokens must be greater than 0');
        setLoading(false);
        return;
      }

      if (formData.price_usd < 0) {
        setError('Price must be 0 or greater');
        setLoading(false);
        return;
      }

      if (formData.description && formData.description.length > 500) {
        setError('Description must be 500 characters or less');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');

      // Prepare addon data
      const addonData = {
        name: formData.name.trim(),
        tokens: parseInt(formData.tokens) || 0,
        price_usd: parseFloat(formData.price_usd) || 0,
        is_active: Boolean(formData.is_active),
        description: formData.description ? formData.description.trim() : ''
      };

      console.log('Submitting addon data:', addonData);
      console.log('Addon ID:', addon?.id);

      let response;
      if (addon?.id) {
        // Update existing addon
        response = await apiClient.put(`/admin/addons/${addon.id}`, addonData, token);
      } else {
        // Create new addon
        response = await apiClient.post('/admin/addons', addonData, token);
      }

      // Wait a moment to ensure the update is processed on the server
      await new Promise(resolve => setTimeout(resolve, 100));

      showSuccess(addon?.id ? 'Addon updated successfully' : 'Addon created successfully');

      // If updating, pass the updated addon data back to parent so it can update local state
      if (addon?.id) {
        const updatedAddon = {
          ...addon,
          ...addonData,
          id: addon.id
        };
        if (onAddonUpdated) {
          onAddonUpdated(updatedAddon);
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving addon:', err);

      // Extract detailed error message from API response
      let errorMessage = 'Failed to save addon';

      // Helper function to safely extract string from error
      const extractString = (value) => {
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value !== null) {
          // Handle objects with code and message
          if (value.message && typeof value.message === 'string') return value.message;
          if (value.msg && typeof value.msg === 'string') return value.msg;
          if (value.error && typeof value.error === 'string') return value.error;
          // Last resort: stringify the object
          return JSON.stringify(value);
        }
        return String(value);
      };

      if (err.data) {
        // Handle validation errors from API
        if (Array.isArray(err.data.detail)) {
          const validationErrors = err.data.detail.map(e => {
            const field = e.loc ? e.loc.join('.') : 'field';
            const msg = extractString(e.msg);
            return `${field}: ${msg}`;
          }).join(', ');
          errorMessage = `Validation error: ${validationErrors}`;
        } else if (err.data.detail) {
          errorMessage = extractString(err.data.detail);
        } else if (err.data.message) {
          errorMessage = extractString(err.data.message);
        } else if (err.data.error) {
          errorMessage = extractString(err.data.error);
        } else if (err.data.code && err.data.message) {
          // Handle {code, message} structure
          errorMessage = extractString(err.data.message);
        }
      } else if (err.error) {
        errorMessage = extractString(err.error);
      } else if (err.message) {
        errorMessage = extractString(err.message);
      }

      // Ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        errorMessage = String(errorMessage);
      }

      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{addon?.id ? 'Edit Addon' : 'Add New Addon'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && !error.includes('Warning') && <div className="error-message">{String(error)}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Addon Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Usage Top-Up"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Tokens *</label>
            <input
              type="number"
              min="1"
              name="tokens"
              value={formData.tokens}
              onChange={handleChange}
              required
              placeholder="e.g., 100"
            />
            <small className="form-help-text">
              Tokens granted per purchase. At capture, users receive 25% of their plan monthly allocation when subscribed; otherwise this value is used (100 = 25% of 400 monthly).
            </small>
          </div>

          <div className="form-group">
            <label>Price (USD) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="price_usd"
              value={formData.price_usd}
              onChange={handleChange}
              required
              placeholder="e.g., 5.00"
            />
            <small className="form-help-text">Price in USD (standard top-up: $5.00)</small>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description of the addon"
              maxLength={500}
              rows={4}
            />
            <small className="form-help-text">Optional description (max 500 characters)</small>
          </div>

          <div className="form-group">
            <label className="active-plan-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="active-plan-checkbox"
              />
              {' '}Active Addon
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? 'Saving...' : (addon?.id ? 'Update' : 'Create')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default AddonModal;

